/** @jsx jsx */
import React, { type ReactNode } from 'react';
import { jsx, cssMap } from '@compiled/react';
import { useLazyLoadQuery, graphql } from 'react-relay';
import { PREMIUM_EDITION } from '@jira/platform__application-editions/src/edition.tsx';
import Link from '@atlaskit/link/link';
import Banner from '@atlaskit/banner/banner';
import WarningIcon from '@atlaskit/icon/core/status-warning';
import { token } from '@atlaskit/tokens';
import { defineMessage, useIntl } from '@atlassian/jira-intl';
import { fg } from '@atlassian/jira-feature-gating';
import { expValEquals } from '@atlassian/jira-feature-experiments';
import { useAppEditions } from '@atlassian/jira-tenant-context-controller/src/components/app-editions/index.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import type {
	LimitsBannerV2Query,
	LimitsBannerV2Query$data,
} from './__generated__/LimitsBannerV2Query.graphql.ts';

// System Configuration General - SO Enabled
const descriptionMessageSystemConfigurationGeneralSOEnabledMarch = defineMessage({
	id: 'platform-ui-banners-limits-banner.LimitsBanner.description-message-system-configuration-general-so-enabled.march',
	defaultMessage:
		'The scheme size limits we announced in August will be enforced starting in March. <link>Read more</link>',
	description:
		'Warning message on system settings home page when Site Optimizer is available, informing users about upcoming scheme size limits enforcement starting in March.',
});

const descriptionMessageSystemConfigurationGeneralSOEnabledApril = defineMessage({
	id: 'platform-ui-banners-limits-banner.LimitsBanner.description-message-system-configuration-general-so-enabled.april',
	defaultMessage:
		'The scheme size limits we announced in August will be enforced starting in April. <link>Read more</link>',
	description:
		'Warning message on system settings home page when Site Optimizer is available, informing users about upcoming scheme size limits enforcement starting in April.',
});

const descriptionMessageSystemConfigurationGeneralSOEnabledMay = defineMessage({
	id: 'platform-ui-banners-limits-banner.LimitsBanner.description-message-system-configuration-general-so-enabled.may',
	defaultMessage:
		'The scheme size limits we announced in August will be enforced starting in May. <link>Read more</link>',
	description:
		'Warning message on system settings home page when Site Optimizer is available, informing users about upcoming scheme size limits enforcement starting in May.',
});

const descriptionMessageSystemConfigurationGeneralSOEnabledJune = defineMessage({
	id: 'platform-ui-banners-limits-banner.LimitsBanner.description-message-system-configuration-general-so-enabled.june',
	defaultMessage:
		'The scheme size limits we announced in August will be enforced starting in June. <link>Read more</link>',
	description:
		'Warning message on system settings home page when Site Optimizer is available, informing users about upcoming scheme size limits enforcement starting in June.',
});

const descriptionMessageSystemConfigurationGeneralSOEnabledSeptember = defineMessage({
	id: 'platform-ui-banners-limits-banner.LimitsBanner.description-message-system-configuration-general-so-enabled.september',
	defaultMessage:
		'The scheme size limits we announced in August will be enforced starting in September. <link>Read more</link>',
	description:
		'Warning message on system settings home page when Site Optimizer is available, informing users about upcoming scheme size limits enforcement starting in September.',
});

// System Configuration General - SO Disabled
const descriptionMessageSystemConfigurationGeneralSODisabledMarch = defineMessage({
	id: 'platform-ui-banners-limits-banner.LimitsBanner.description-message-system-configuration-general-so-disabled.march',
	defaultMessage:
		'The scheme size limits we announced in August will be enforced starting in March. <link>Read more</link>',
	description:
		'Warning message on system settings home page when Site Optimizer is not available, informing users about upcoming scheme size limits enforcement starting in March.',
});

const descriptionMessageSystemConfigurationGeneralSODisabledApril = defineMessage({
	id: 'platform-ui-banners-limits-banner.LimitsBanner.description-message-system-configuration-general-so-disabled.april',
	defaultMessage:
		'The scheme size limits we announced in August will be enforced starting in April. <link>Read more</link>',
	description:
		'Warning message on system settings home page when Site Optimizer is not available, informing users about upcoming scheme size limits enforcement starting in April.',
});

const descriptionMessageSystemConfigurationGeneralSODisabledMay = defineMessage({
	id: 'platform-ui-banners-limits-banner.LimitsBanner.description-message-system-configuration-general-so-disabled.may',
	defaultMessage:
		'The scheme size limits we announced in August will be enforced starting in May. <link>Read more</link>',
	description:
		'Warning message on system settings home page when Site Optimizer is not available, informing users about upcoming scheme size limits enforcement starting in May.',
});

const descriptionMessageSystemConfigurationGeneralSODisabledJune = defineMessage({
	id: 'platform-ui-banners-limits-banner.LimitsBanner.description-message-system-configuration-general-so-disabled.june',
	defaultMessage:
		'The scheme size limits we announced in August will be enforced starting in June. <link>Read more</link>',
	description:
		'Warning message on system settings home page when Site Optimizer is not available, informing users about upcoming scheme size limits enforcement starting in June.',
});

const descriptionMessageSystemConfigurationGeneralSODisabledSeptember = defineMessage({
	id: 'platform-ui-banners-limits-banner.LimitsBanner.description-message-system-configuration-general-so-disabled.september',
	defaultMessage:
		'The scheme size limits we announced in August will be enforced starting in September. <link>Read more</link>',
	description:
		'Warning message on system settings home page when Site Optimizer is not available, informing users about upcoming scheme size limits enforcement starting in September.',
});

// Issue Type - SO Enabled
const descriptionMessageIssueTypeSOEnabledMarch = defineMessage({
	id: 'platform-ui-banners-limits-banner.LimitsBanner.description-message-issue-type-so-enabled.march',
	defaultMessage:
		'Starting in March, work type schemes will be limited to {limit, plural, one {# work type} other {# work types}}. <link>Use the Site Optimizer</link> to fix schemes that exceed this limit.',
	description:
		'Warning message on work type page when Site Optimizer is available, informing users about the work type limit starting in March. {limit} is a number representing the maximum allowed work types, with plural handling for singular and plural forms.',
});

const descriptionMessageIssueTypeSOEnabledApril = defineMessage({
	id: 'platform-ui-banners-limits-banner.LimitsBanner.description-message-issue-type-so-enabled.april',
	defaultMessage:
		'Starting in April, work type schemes will be limited to {limit, plural, one {# work type} other {# work types}}. <link>Use the Site Optimizer</link> to fix schemes that exceed this limit.',
	description:
		'Warning message on work type page when Site Optimizer is available, informing users about the work type limit starting in April. {limit} is a number representing the maximum allowed work types, with plural handling for singular and plural forms.',
});

const descriptionMessageIssueTypeSOEnabledMay = defineMessage({
	id: 'platform-ui-banners-limits-banner.LimitsBanner.description-message-issue-type-so-enabled.may',
	defaultMessage:
		'Starting in May, work type schemes will be limited to {limit, plural, one {# work type} other {# work types}}. <link>Use the Site Optimizer</link> to fix schemes that exceed this limit.',
	description:
		'Warning message on work type page when Site Optimizer is available, informing users about the work type limit starting in May. {limit} is a number representing the maximum allowed work types, with plural handling for singular and plural forms.',
});

const descriptionMessageIssueTypeSOEnabledJune = defineMessage({
	id: 'platform-ui-banners-limits-banner.LimitsBanner.description-message-issue-type-so-enabled.june',
	defaultMessage:
		'Starting in June, work type schemes will be limited to {limit, plural, one {# work type} other {# work types}}. <link>Use the Site Optimizer</link> to fix schemes that exceed this limit.',
	description:
		'Warning message on work type page when Site Optimizer is available, informing users about the work type limit starting in June. {limit} is a number representing the maximum allowed work types, with plural handling for singular and plural forms.',
});

const descriptionMessageIssueTypeSOEnabledSeptember = defineMessage({
	id: 'platform-ui-banners-limits-banner.LimitsBanner.description-message-issue-type-so-enabled.september',
	defaultMessage:
		'Starting in September, work type schemes will be limited to {limit, plural, one {# work type} other {# work types}}. <link>Use the Site Optimizer</link> to fix schemes that exceed this limit.',
	description:
		'Warning message on work type page when Site Optimizer is available, informing users about the work type limit starting in September. {limit} is a number representing the maximum allowed work types, with plural handling for singular and plural forms.',
});

// Issue Type - SO Disabled
const descriptionMessageIssueTypeSODisabledMarch = defineMessage({
	id: 'platform-ui-banners-limits-banner.LimitsBanner.description-message-issue-type-so-disabled.march',
	defaultMessage:
		'Starting in March, work type schemes will be limited to {limit, plural, one {# work type} other {# work types}}. <link>How to optimize your schemes</link>',
	description:
		'Warning message on work type page when Site Optimizer is not available, informing users about the work type limit starting in March. {limit} is a number representing the maximum allowed work types, with plural handling for singular and plural forms.',
});

const descriptionMessageIssueTypeSODisabledApril = defineMessage({
	id: 'platform-ui-banners-limits-banner.LimitsBanner.description-message-issue-type-so-disabled.april',
	defaultMessage:
		'Starting in April, work type schemes will be limited to {limit, plural, one {# work type} other {# work types}}. <link>How to optimize your schemes</link>',
	description:
		'Warning message on work type page when Site Optimizer is not available, informing users about the work type limit starting in April. {limit} is a number representing the maximum allowed work types, with plural handling for singular and plural forms.',
});

const descriptionMessageIssueTypeSODisabledMay = defineMessage({
	id: 'platform-ui-banners-limits-banner.LimitsBanner.description-message-issue-type-so-disabled.may',
	defaultMessage:
		'Starting in May, work type schemes will be limited to {limit, plural, one {# work type} other {# work types}}. <link>How to optimize your schemes</link>',
	description:
		'Warning message on work type page when Site Optimizer is not available, informing users about the work type limit starting in May. {limit} is a number representing the maximum allowed work types, with plural handling for singular and plural forms.',
});

const descriptionMessageIssueTypeSODisabledJune = defineMessage({
	id: 'platform-ui-banners-limits-banner.LimitsBanner.description-message-issue-type-so-disabled.june',
	defaultMessage:
		'Starting in June, work type schemes will be limited to {limit, plural, one {# work type} other {# work types}}. <link>How to optimize your schemes</link>',
	description:
		'Warning message on work type page when Site Optimizer is not available, informing users about the work type limit starting in June. {limit} is a number representing the maximum allowed work types, with plural handling for singular and plural forms.',
});

const descriptionMessageIssueTypeSODisabledSeptember = defineMessage({
	id: 'platform-ui-banners-limits-banner.LimitsBanner.description-message-issue-type-so-disabled.september',
	defaultMessage:
		'Starting in September, work type schemes will be limited to {limit, plural, one {# work type} other {# work types}}. <link>How to optimize your schemes</link>',
	description:
		'Warning message on work type page when Site Optimizer is not available, informing users about the work type limit starting in September. {limit} is a number representing the maximum allowed work types, with plural handling for singular and plural forms.',
});

// Issue Type Scheme - SO Enabled
const descriptionMessageIssueTypeSchemeSOEnabledMarch = defineMessage({
	id: 'platform-ui-banners-limits-banner.LimitsBannerV2.description-message-issue-type-scheme-so-enabled.march',
	defaultMessage:
		'Starting in March, work type schemes will be limited to {limit, plural, one {# work type} other {# work types}}. <link>Use the Site Optimizer</link> to fix schemes that exceed this limit.',
	description:
		'Warning message on work type scheme page when Site Optimizer is available, informing users about the work type limit starting in March. {limit} is a number representing the maximum allowed work types, with plural handling for singular and plural forms.',
});

const descriptionMessageIssueTypeSchemeSOEnabledApril = defineMessage({
	id: 'platform-ui-banners-limits-banner.LimitsBannerV2.description-message-issue-type-scheme-so-enabled.april',
	defaultMessage:
		'Starting in April, work type schemes will be limited to {limit, plural, one {# work type} other {# work types}}. <link>Use the Site Optimizer</link> to fix schemes that exceed this limit.',
	description:
		'Warning message on work type scheme page when Site Optimizer is available, informing users about the work type limit starting in April. {limit} is a number representing the maximum allowed work types, with plural handling for singular and plural forms.',
});

const descriptionMessageIssueTypeSchemeSOEnabledMay = defineMessage({
	id: 'platform-ui-banners-limits-banner.LimitsBannerV2.description-message-issue-type-scheme-so-enabled.may',
	defaultMessage:
		'Starting in May, work type schemes will be limited to {limit, plural, one {# work type} other {# work types}}. <link>Use the Site Optimizer</link> to fix schemes that exceed this limit.',
	description:
		'Warning message on work type scheme page when Site Optimizer is available, informing users about the work type limit starting in May. {limit} is a number representing the maximum allowed work types, with plural handling for singular and plural forms.',
});

const descriptionMessageIssueTypeSchemeSOEnabledJune = defineMessage({
	id: 'platform-ui-banners-limits-banner.LimitsBannerV2.description-message-issue-type-scheme-so-enabled.june',
	defaultMessage:
		'Starting in June, work type schemes will be limited to {limit, plural, one {# work type} other {# work types}}. <link>Use the Site Optimizer</link> to fix schemes that exceed this limit.',
	description:
		'Warning message on work type scheme page when Site Optimizer is available, informing users about the work type limit starting in June. {limit} is a number representing the maximum allowed work types, with plural handling for singular and plural forms.',
});

const descriptionMessageIssueTypeSchemeSOEnabledSeptember = defineMessage({
	id: 'platform-ui-banners-limits-banner.LimitsBannerV2.description-message-issue-type-scheme-so-enabled.september',
	defaultMessage:
		'Starting in September, work type schemes will be limited to {limit, plural, one {# work type} other {# work types}}. <link>Use the Site Optimizer</link> to fix schemes that exceed this limit.',
	description:
		'Warning message on work type scheme page when Site Optimizer is available, informing users about the work type limit starting in September. {limit} is a number representing the maximum allowed work types, with plural handling for singular and plural forms.',
});

// Issue Type Scheme - SO Disabled
const descriptionMessageIssueTypeSchemeSODisabledMarch = defineMessage({
	id: 'platform-ui-banners-limits-banner.LimitsBannerV2.description-message-issue-type-scheme-so-disabled.march',
	defaultMessage:
		'Starting in March, work type schemes will be limited to {limit, plural, one {# work type} other {# work types}}. <link>How to optimize your schemes</link>',
	description:
		'Warning message on work type scheme page when Site Optimizer is not available, informing users about the work type limit starting in March. {limit} is a number representing the maximum allowed work types, with plural handling for singular and plural forms.',
});

const descriptionMessageIssueTypeSchemeSODisabledApril = defineMessage({
	id: 'platform-ui-banners-limits-banner.LimitsBannerV2.description-message-issue-type-scheme-so-disabled.april',
	defaultMessage:
		'Starting in April, work type schemes will be limited to {limit, plural, one {# work type} other {# work types}}. <link>How to optimize your schemes</link>',
	description:
		'Warning message on work type scheme page when Site Optimizer is not available, informing users about the work type limit starting in April. {limit} is a number representing the maximum allowed work types, with plural handling for singular and plural forms.',
});

const descriptionMessageIssueTypeSchemeSODisabledMay = defineMessage({
	id: 'platform-ui-banners-limits-banner.LimitsBannerV2.description-message-issue-type-scheme-so-disabled.may',
	defaultMessage:
		'Starting in May, work type schemes will be limited to {limit, plural, one {# work type} other {# work types}}. <link>How to optimize your schemes</link>',
	description:
		'Warning message on work type scheme page when Site Optimizer is not available, informing users about the work type limit starting in May. {limit} is a number representing the maximum allowed work types, with plural handling for singular and plural forms.',
});

const descriptionMessageIssueTypeSchemeSODisabledJune = defineMessage({
	id: 'platform-ui-banners-limits-banner.LimitsBannerV2.description-message-issue-type-scheme-so-disabled.june',
	defaultMessage:
		'Starting in June, work type schemes will be limited to {limit, plural, one {# work type} other {# work types}}. <link>How to optimize your schemes</link>',
	description:
		'Warning message on work type scheme page when Site Optimizer is not available, informing users about the work type limit starting in June. {limit} is a number representing the maximum allowed work types, with plural handling for singular and plural forms.',
});

const descriptionMessageIssueTypeSchemeSODisabledSeptember = defineMessage({
	id: 'platform-ui-banners-limits-banner.LimitsBannerV2.description-message-issue-type-scheme-so-disabled.september',
	defaultMessage:
		'Starting in September, work type schemes will be limited to {limit, plural, one {# work type} other {# work types}}. <link>How to optimize your schemes</link>',
	description:
		'Warning message on work type scheme page when Site Optimizer is not available, informing users about the work type limit starting in September. {limit} is a number representing the maximum allowed work types, with plural handling for singular and plural forms.',
});

// Field - SO Enabled
const descriptionMessageFieldSOEnabledMarch = defineMessage({
	id: 'platform-ui-banners-limits-banner.LimitsBannerV2.description-message-field-so-enabled.march',
	defaultMessage:
		'Starting in March, field configuration schemes will be limited to {limit, plural, one {# field} other {# fields}}. <link>Use the Site Optimizer</link> to remove fields from schemes that exceed this limit.',
	description:
		'Warning message on field page when Site Optimizer is available, informing users about the field limit starting in March. {limit} is a number representing the maximum allowed fields, with plural handling for singular and plural forms.',
});

const descriptionMessageFieldSOEnabledApril = defineMessage({
	id: 'platform-ui-banners-limits-banner.LimitsBannerV2.description-message-field-so-enabled.april',
	defaultMessage:
		'Starting in April, field configuration schemes will be limited to {limit, plural, one {# field} other {# fields}}. <link>Use the Site Optimizer</link> to remove fields from schemes that exceed this limit.',
	description:
		'Warning message on field page when Site Optimizer is available, informing users about the field limit starting in April. {limit} is a number representing the maximum allowed fields, with plural handling for singular and plural forms.',
});

const descriptionMessageFieldSOEnabledMay = defineMessage({
	id: 'platform-ui-banners-limits-banner.LimitsBannerV2.description-message-field-so-enabled.may',
	defaultMessage:
		'Starting in May, field configuration schemes will be limited to {limit, plural, one {# field} other {# fields}}. <link>Use the Site Optimizer</link> to remove fields from schemes that exceed this limit.',
	description:
		'Warning message on field page when Site Optimizer is available, informing users about the field limit starting in May. {limit} is a number representing the maximum allowed fields, with plural handling for singular and plural forms.',
});

const descriptionMessageFieldSOEnabledJune = defineMessage({
	id: 'platform-ui-banners-limits-banner.LimitsBannerV2.description-message-field-so-enabled.june',
	defaultMessage:
		'Starting in June, field configuration schemes will be limited to {limit, plural, one {# field} other {# fields}}. <link>Use the Site Optimizer</link> to remove fields from schemes that exceed this limit.',
	description:
		'Warning message on field page when Site Optimizer is available, informing users about the field limit starting in June. {limit} is a number representing the maximum allowed fields, with plural handling for singular and plural forms.',
});

const descriptionMessageFieldSOEnabledSeptember = defineMessage({
	id: 'platform-ui-banners-limits-banner.LimitsBannerV2.description-message-field-so-enabled.september',
	defaultMessage:
		'Starting in September, field configuration schemes will be limited to {limit, plural, one {# field} other {# fields}}. <link>Use the Site Optimizer</link> to remove fields from schemes that exceed this limit.',
	description:
		'Warning message on field page when Site Optimizer is available, informing users about the field limit starting in September. {limit} is a number representing the maximum allowed fields, with plural handling for singular and plural forms.',
});

// Field - SO Disabled
const descriptionMessageFieldSODisabledMarch = defineMessage({
	id: 'platform-ui-banners-limits-banner.LimitsBannerV2.description-message-field-so-disabled.march',
	defaultMessage:
		'Starting in March, field configuration schemes will be limited to {limit, plural, one {# field} other {# fields}}. <link>How to optimize your schemes</link>',
	description:
		'Warning message on field page when Site Optimizer is not available, informing users about the field limit starting in March. {limit} is a number representing the maximum allowed fields, with plural handling for singular and plural forms.',
});

const descriptionMessageFieldSODisabledApril = defineMessage({
	id: 'platform-ui-banners-limits-banner.LimitsBannerV2.description-message-field-so-disabled.april',
	defaultMessage:
		'Starting in April, field configuration schemes will be limited to {limit, plural, one {# field} other {# fields}}. <link>How to optimize your schemes</link>',
	description:
		'Warning message on field page when Site Optimizer is not available, informing users about the field limit starting in April. {limit} is a number representing the maximum allowed fields, with plural handling for singular and plural forms.',
});

const descriptionMessageFieldSODisabledMay = defineMessage({
	id: 'platform-ui-banners-limits-banner.LimitsBannerV2.description-message-field-so-disabled.may',
	defaultMessage:
		'Starting in May, field configuration schemes will be limited to {limit, plural, one {# field} other {# fields}}. <link>How to optimize your schemes</link>',
	description:
		'Warning message on field page when Site Optimizer is not available, informing users about the field limit starting in May. {limit} is a number representing the maximum allowed fields, with plural handling for singular and plural forms.',
});

const descriptionMessageFieldSODisabledJune = defineMessage({
	id: 'platform-ui-banners-limits-banner.LimitsBannerV2.description-message-field-so-disabled.june',
	defaultMessage:
		'Starting in June, field configuration schemes will be limited to {limit, plural, one {# field} other {# fields}}. <link>How to optimize your schemes</link>',
	description:
		'Warning message on field page when Site Optimizer is not available, informing users about the field limit starting in June. {limit} is a number representing the maximum allowed fields, with plural handling for singular and plural forms.',
});

const descriptionMessageFieldSODisabledSeptember = defineMessage({
	id: 'platform-ui-banners-limits-banner.LimitsBannerV2.description-message-field-so-disabled.september',
	defaultMessage:
		'Starting in September, field configuration schemes will be limited to {limit, plural, one {# field} other {# fields}}. <link>How to optimize your schemes</link>',
	description:
		'Warning message on field page when Site Optimizer is not available, informing users about the field limit starting in September. {limit} is a number representing the maximum allowed fields, with plural handling for singular and plural forms.',
});

// Field Configuration Description - SO Enabled
const descriptionMessageFieldConfigurationDescriptionSOEnabledMarch = defineMessage({
	id: 'platform-ui-banners-limits-banner.LimitsBannerV2.description-message-field-configuration-description-so-enabled.march',
	defaultMessage:
		'Starting in March, field configuration schemes will be limited to {limit, plural, one {# field} other {# fields}}. <link>Use the Site Optimizer</link> to remove fields from schemes that exceed this limit.',
	description:
		'Warning message on field configuration description page when Site Optimizer is available, informing users about the field limit starting in March. {limit} is a number representing the maximum allowed fields, with plural handling for singular and plural forms.',
});

const descriptionMessageFieldConfigurationDescriptionSOEnabledApril = defineMessage({
	id: 'platform-ui-banners-limits-banner.LimitsBannerV2.description-message-field-configuration-description-so-enabled.april',
	defaultMessage:
		'Starting in April, field configuration schemes will be limited to {limit, plural, one {# field} other {# fields}}. <link>Use the Site Optimizer</link> to remove fields from schemes that exceed this limit.',
	description:
		'Warning message on field configuration description page when Site Optimizer is available, informing users about the field limit starting in April. {limit} is a number representing the maximum allowed fields, with plural handling for singular and plural forms.',
});

const descriptionMessageFieldConfigurationDescriptionSOEnabledMay = defineMessage({
	id: 'platform-ui-banners-limits-banner.LimitsBannerV2.description-message-field-configuration-description-so-enabled.may',
	defaultMessage:
		'Starting in May, field configuration schemes will be limited to {limit, plural, one {# field} other {# fields}}. <link>Use the Site Optimizer</link> to remove fields from schemes that exceed this limit.',
	description:
		'Warning message on field configuration description page when Site Optimizer is available, informing users about the field limit starting in May. {limit} is a number representing the maximum allowed fields, with plural handling for singular and plural forms.',
});

const descriptionMessageFieldConfigurationDescriptionSOEnabledJune = defineMessage({
	id: 'platform-ui-banners-limits-banner.LimitsBannerV2.description-message-field-configuration-description-so-enabled.june',
	defaultMessage:
		'Starting in June, field configuration schemes will be limited to {limit, plural, one {# field} other {# fields}}. <link>Use the Site Optimizer</link> to remove fields from schemes that exceed this limit.',
	description:
		'Warning message on field configuration description page when Site Optimizer is available, informing users about the field limit starting in June. {limit} is a number representing the maximum allowed fields, with plural handling for singular and plural forms.',
});

const descriptionMessageFieldConfigurationDescriptionSOEnabledSeptember = defineMessage({
	id: 'platform-ui-banners-limits-banner.LimitsBannerV2.description-message-field-configuration-description-so-enabled.september',
	defaultMessage:
		'Starting in September, field configuration schemes will be limited to {limit, plural, one {# field} other {# fields}}. <link>Use the Site Optimizer</link> to remove fields from schemes that exceed this limit.',
	description:
		'Warning message on field configuration description page when Site Optimizer is available, informing users about the field limit starting in September. {limit} is a number representing the maximum allowed fields, with plural handling for singular and plural forms.',
});

// Field Configuration Description - SO Disabled
const descriptionMessageFieldConfigurationDescriptionSODisabledMarch = defineMessage({
	id: 'platform-ui-banners-limits-banner.LimitsBannerV2.description-message-field-configuration-description-so-disabled.march',
	defaultMessage:
		'Starting in March, field configuration schemes will be limited to {limit, plural, one {# field} other {# fields}}. <link>How to optimize your schemes</link>',
	description:
		'Warning message on field configuration description page when Site Optimizer is not available, informing users about the field limit starting in March. {limit} is a number representing the maximum allowed fields, with plural handling for singular and plural forms.',
});

const descriptionMessageFieldConfigurationDescriptionSODisabledApril = defineMessage({
	id: 'platform-ui-banners-limits-banner.LimitsBannerV2.description-message-field-configuration-description-so-disabled.april',
	defaultMessage:
		'Starting in April, field configuration schemes will be limited to {limit, plural, one {# field} other {# fields}}. <link>How to optimize your schemes</link>',
	description:
		'Warning message on field configuration description page when Site Optimizer is not available, informing users about the field limit starting in April. {limit} is a number representing the maximum allowed fields, with plural handling for singular and plural forms.',
});

const descriptionMessageFieldConfigurationDescriptionSODisabledMay = defineMessage({
	id: 'platform-ui-banners-limits-banner.LimitsBannerV2.description-message-field-configuration-description-so-disabled.may',
	defaultMessage:
		'Starting in May, field configuration schemes will be limited to {limit, plural, one {# field} other {# fields}}. <link>How to optimize your schemes</link>',
	description:
		'Warning message on field configuration description page when Site Optimizer is not available, informing users about the field limit starting in May. {limit} is a number representing the maximum allowed fields, with plural handling for singular and plural forms.',
});

const descriptionMessageFieldConfigurationDescriptionSODisabledJune = defineMessage({
	id: 'platform-ui-banners-limits-banner.LimitsBannerV2.description-message-field-configuration-description-so-disabled.june',
	defaultMessage:
		'Starting in June, field configuration schemes will be limited to {limit, plural, one {# field} other {# fields}}. <link>How to optimize your schemes</link>',
	description:
		'Warning message on field configuration description page when Site Optimizer is not available, informing users about the field limit starting in June. {limit} is a number representing the maximum allowed fields, with plural handling for singular and plural forms.',
});

const descriptionMessageFieldConfigurationDescriptionSODisabledSeptember = defineMessage({
	id: 'platform-ui-banners-limits-banner.LimitsBannerV2.description-message-field-configuration-description-so-disabled.september',
	defaultMessage:
		'Starting in September, field configuration schemes will be limited to {limit, plural, one {# field} other {# fields}}. <link>How to optimize your schemes</link>',
	description:
		'Warning message on field configuration description page when Site Optimizer is not available, informing users about the field limit starting in September. {limit} is a number representing the maximum allowed fields, with plural handling for singular and plural forms.',
});

const linkStyles = cssMap({
	content: {
		color: token('color.link'),
	},
});

type LIMITS_BANNER_PAGES =
	| 'SYSTEM_SETTINGS_HOME_PAGE'
	| 'WORK_TYPE_PAGE'
	| 'WORK_TYPE_SCHEME_PAGE'
	| 'FIELD_PAGE'
	| 'FIELD_CONFIGURATION_DESCRIPTION_PAGE';

type Message = {
	id: string;
	defaultMessage: string;
	description: string;
};

const FIELDS_ENTITY_KEY = 'fields.per.project.limit';
const ISSUE_TYPES_ENTITY_KEY = 'issue.types.per.project.limit';

const DEFAULT_FIELDS_LIMIT = 700;
const DEFAULT_ISSUE_TYPES_LIMIT = 150;
const LIMIT_VALUES_FILTER_KEYS = [FIELDS_ENTITY_KEY, ISSUE_TYPES_ENTITY_KEY];

type LimitValues = {
	fieldsLimit: number;
	issueTypesLimit: number;
};

const parseLimitValues = (response: LimitsBannerV2Query$data): LimitValues => {
	let fieldsLimit = DEFAULT_FIELDS_LIMIT;
	let issueTypesLimit = DEFAULT_ISSUE_TYPES_LIMIT;

	const limitValues = response?.jira?.limitValues;
	if (limitValues) {
		for (const entry of limitValues) {
			if (entry?.key === FIELDS_ENTITY_KEY && entry?.limitValue != null) {
				fieldsLimit = entry.limitValue;
			}
			if (entry?.key === ISSUE_TYPES_ENTITY_KEY && entry?.limitValue != null) {
				issueTypesLimit = entry.limitValue;
			}
		}
	}

	return { fieldsLimit, issueTypesLimit };
};

type PageMessages = {
	march: Message;
	april: Message;
	may: Message;
	june: Message;
	september: Message;
};

type MessageDataWithMonths = {
	messages: PageMessages;
	primaryLink: string;
	secondaryLink?: string;
};

const LIMITS_BANNER_MESSAGES_SO_ENABLED = new Map<LIMITS_BANNER_PAGES, MessageDataWithMonths>([
	[
		'SYSTEM_SETTINGS_HOME_PAGE',
		{
			messages: {
				march: descriptionMessageSystemConfigurationGeneralSOEnabledMarch,
				april: descriptionMessageSystemConfigurationGeneralSOEnabledApril,
				may: descriptionMessageSystemConfigurationGeneralSOEnabledMay,
				june: descriptionMessageSystemConfigurationGeneralSOEnabledJune,
				september: descriptionMessageSystemConfigurationGeneralSOEnabledSeptember,
			},
			primaryLink:
				'https://support.atlassian.com/jira-cloud-administration/docs/optimize-fields-in-your-project/',
		},
	],
	[
		'WORK_TYPE_PAGE',
		{
			messages: {
				march: descriptionMessageIssueTypeSOEnabledMarch,
				april: descriptionMessageIssueTypeSOEnabledApril,
				may: descriptionMessageIssueTypeSOEnabledMay,
				june: descriptionMessageIssueTypeSOEnabledJune,
				september: descriptionMessageIssueTypeSOEnabledSeptember,
			},
			primaryLink: '/jira/settings/system/performance/overview',
			secondaryLink:
				'/jira/settings/system/performance/dashboard?tab=limitsAndCleanupPerformancePage',
		},
	],
	[
		'WORK_TYPE_SCHEME_PAGE',
		{
			messages: {
				march: descriptionMessageIssueTypeSchemeSOEnabledMarch,
				april: descriptionMessageIssueTypeSchemeSOEnabledApril,
				may: descriptionMessageIssueTypeSchemeSOEnabledMay,
				june: descriptionMessageIssueTypeSchemeSOEnabledJune,
				september: descriptionMessageIssueTypeSchemeSOEnabledSeptember,
			},
			primaryLink: '/jira/settings/system/performance/overview',
			secondaryLink:
				'/jira/settings/system/performance/dashboard?tab=limitsAndCleanupPerformancePage',
		},
	],
	[
		'FIELD_PAGE',
		{
			messages: {
				march: descriptionMessageFieldSOEnabledMarch,
				april: descriptionMessageFieldSOEnabledApril,
				may: descriptionMessageFieldSOEnabledMay,
				june: descriptionMessageFieldSOEnabledJune,
				september: descriptionMessageFieldSOEnabledSeptember,
			},
			primaryLink: '/jira/settings/system/performance/overview',
			secondaryLink:
				'/jira/settings/system/performance/dashboard?tab=limitsAndCleanupPerformancePage',
		},
	],
	[
		'FIELD_CONFIGURATION_DESCRIPTION_PAGE',
		{
			messages: {
				march: descriptionMessageFieldConfigurationDescriptionSOEnabledMarch,
				april: descriptionMessageFieldConfigurationDescriptionSOEnabledApril,
				may: descriptionMessageFieldConfigurationDescriptionSOEnabledMay,
				june: descriptionMessageFieldConfigurationDescriptionSOEnabledJune,
				september: descriptionMessageFieldConfigurationDescriptionSOEnabledSeptember,
			},
			primaryLink: '/jira/settings/system/performance/overview',
			secondaryLink:
				'/jira/settings/system/performance/dashboard?tab=limitsAndCleanupPerformancePage',
		},
	],
]);

const LIMITS_BANNER_MESSAGES_SO_DISABLED = new Map<LIMITS_BANNER_PAGES, MessageDataWithMonths>([
	[
		'SYSTEM_SETTINGS_HOME_PAGE',
		{
			messages: {
				march: descriptionMessageSystemConfigurationGeneralSODisabledMarch,
				april: descriptionMessageSystemConfigurationGeneralSODisabledApril,
				may: descriptionMessageSystemConfigurationGeneralSODisabledMay,
				june: descriptionMessageSystemConfigurationGeneralSODisabledJune,
				september: descriptionMessageSystemConfigurationGeneralSODisabledSeptember,
			},
			primaryLink:
				'https://support.atlassian.com/jira-cloud-administration/docs/optimize-fields-in-your-project/',
		},
	],
	[
		'WORK_TYPE_PAGE',
		{
			messages: {
				march: descriptionMessageIssueTypeSODisabledMarch,
				april: descriptionMessageIssueTypeSODisabledApril,
				may: descriptionMessageIssueTypeSODisabledMay,
				june: descriptionMessageIssueTypeSODisabledJune,
				september: descriptionMessageIssueTypeSODisabledSeptember,
			},
			primaryLink:
				'https://support.atlassian.com/jira-cloud-administration/docs/optimize-work-types/',
		},
	],
	[
		'WORK_TYPE_SCHEME_PAGE',
		{
			messages: {
				march: descriptionMessageIssueTypeSchemeSODisabledMarch,
				april: descriptionMessageIssueTypeSchemeSODisabledApril,
				may: descriptionMessageIssueTypeSchemeSODisabledMay,
				june: descriptionMessageIssueTypeSchemeSODisabledJune,
				september: descriptionMessageIssueTypeSchemeSODisabledSeptember,
			},
			primaryLink:
				'https://support.atlassian.com/jira-cloud-administration/docs/optimize-work-types/',
		},
	],
	[
		'FIELD_PAGE',
		{
			messages: {
				march: descriptionMessageFieldSODisabledMarch,
				april: descriptionMessageFieldSODisabledApril,
				may: descriptionMessageFieldSODisabledMay,
				june: descriptionMessageFieldSODisabledJune,
				september: descriptionMessageFieldSODisabledSeptember,
			},
			primaryLink:
				'https://support.atlassian.com/jira-cloud-administration/docs/optimize-fields-in-your-project/',
		},
	],
	[
		'FIELD_CONFIGURATION_DESCRIPTION_PAGE',
		{
			messages: {
				march: descriptionMessageFieldConfigurationDescriptionSODisabledMarch,
				april: descriptionMessageFieldConfigurationDescriptionSODisabledApril,
				may: descriptionMessageFieldConfigurationDescriptionSODisabledMay,
				june: descriptionMessageFieldConfigurationDescriptionSODisabledJune,
				september: descriptionMessageFieldConfigurationDescriptionSODisabledSeptember,
			},
			primaryLink:
				'https://support.atlassian.com/jira-cloud-administration/docs/optimize-fields-in-your-project/',
		},
	],
]);

export type Props = {
	page: LIMITS_BANNER_PAGES;
};

const FIELD_PAGES: LIMITS_BANNER_PAGES[] = ['FIELD_PAGE', 'FIELD_CONFIGURATION_DESCRIPTION_PAGE'];

export const LimitsBannerV2 = ({ page }: Props): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const appEditions = useAppEditions();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const cloudId = useCloudId();
	const response = useLazyLoadQuery<LimitsBannerV2Query>(
		graphql`
			query LimitsBannerV2Query($cloudId: ID!, $filterKeys: [String!]!) {
				jira @optIn(to: "JiraResourceUsageMetricLimitValue") {
					limitValues(cloudId: $cloudId, filterKeys: $filterKeys) {
						key
						limitValue
					}
				}
			}
		`,
		{
			cloudId,
			filterKeys: LIMIT_VALUES_FILTER_KEYS,
		},
	);
	const limitValues = parseLimitValues(response);

	const isPremium = appEditions?.software === PREMIUM_EDITION;

	const dataSOEnabled = LIMITS_BANNER_MESSAGES_SO_ENABLED.get(page);
	const dataSODisabled = LIMITS_BANNER_MESSAGES_SO_DISABLED.get(page);

	if (!dataSOEnabled || !dataSODisabled) {
		return <></>;
	}

	let messageSOEnabled: Message;
	let messageSODisabled: Message;
	if (
		expValEquals('pre_enforcement_limits_contextual_banner_segments', 'value', 'phase2', 'default')
	) {
		messageSOEnabled = dataSOEnabled.messages.april;
		messageSODisabled = dataSODisabled.messages.april;
	} else if (
		expValEquals('pre_enforcement_limits_contextual_banner_segments', 'value', 'phase3', 'default')
	) {
		messageSOEnabled = dataSOEnabled.messages.may;
		messageSODisabled = dataSODisabled.messages.may;
	} else if (
		expValEquals('pre_enforcement_limits_contextual_banner_segments', 'value', 'phase4', 'default')
	) {
		messageSOEnabled = dataSOEnabled.messages.june;
		messageSODisabled = dataSODisabled.messages.june;
	} else if (
		expValEquals(
			'pre_enforcement_limits_contextual_banner_segments',
			'value',
			'TMP_segment',
			'default',
		)
	) {
		messageSOEnabled = dataSOEnabled.messages.june;
		messageSODisabled = dataSODisabled.messages.june;
	} else if (
		expValEquals(
			'pre_enforcement_limits_contextual_banner_segments',
			'value',
			'already_migrated_segment',
			'default',
		)
	) {
		messageSOEnabled = dataSOEnabled.messages.september;
		messageSODisabled = dataSODisabled.messages.september;
	} else if (
		expValEquals(
			'pre_enforcement_limits_contextual_banner_segments',
			'value',
			'migrating_segment',
			'default',
		)
	) {
		messageSOEnabled = dataSOEnabled.messages.september;
		messageSODisabled = dataSODisabled.messages.september;
	} else {
		messageSOEnabled = dataSOEnabled.messages.march;
		messageSODisabled = dataSODisabled.messages.march;
	}

	let linkSOEnabled = dataSOEnabled.primaryLink;
	const linkSODisabled = dataSODisabled.primaryLink;

	if (dataSOEnabled.secondaryLink !== undefined) {
		linkSOEnabled = fg('enable_cfo_dashboard')
			? dataSOEnabled.secondaryLink
			: dataSODisabled.primaryLink;
	}

	const handleLinkClick = () => {
		fireUIAnalytics(
			createAnalyticsEvent({
				actionSubject: 'preEnforcementLimitsBannerLink',
				action: 'clicked',
			}),
		);
	};

	return (
		<Banner appearance="warning" icon={<WarningIcon label="" />}>
			{isPremium
				? formatMessage(messageSOEnabled, {
						limit: FIELD_PAGES.includes(page)
							? limitValues.fieldsLimit
							: limitValues.issueTypesLimit,
						link: (chunks: ReactNode) => (
							<span css={linkStyles.content}>
								<Link href={linkSOEnabled} target="_blank" onClick={handleLinkClick}>
									{chunks}
								</Link>
							</span>
						),
					})
				: formatMessage(messageSODisabled, {
						limit: FIELD_PAGES.includes(page)
							? limitValues.fieldsLimit
							: limitValues.issueTypesLimit,
						link: (chunks: ReactNode) => (
							<span>
								<Link href={linkSODisabled} target="_blank" onClick={handleLinkClick}>
									{chunks}
								</Link>
							</span>
						),
					})}
		</Banner>
	);
};

export default LimitsBannerV2;
